import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert
} from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

const _stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

const _CheckoutForm = ({ packageInfo, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create payment intent
      const response = await api.post('/api/ai/credits/purchase', {
        creditPackage: packageInfo.id
      });

      const { clientSecret } = response.data;

      // Confirm payment
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        }
      });

      if (confirmError) {
        setError(confirmError.message);
        setLoading(false);
        onError(confirmError.message);
      } else if (paymentIntent.status === 'succeeded') {
        setLoading(false);
        onSuccess(packageInfo);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Payment failed. Please try again.');
      setLoading(false);
      onError(err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 2 }}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
            },
          }}
        />
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={!stripe || loading}
        sx={{ mt: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : `Purchase for £${packageInfo.price.toFixed(2)}`}
      </Button>
    </form>
  );
};

const CreditPurchaseModal = ({ open, onClose, onPurchaseSuccess, redirectTo = 'lumi' }) => {
  const _styles = useThemeStyles();
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      loadPricing();
    }
  }, [open]);

  const loadPricing = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/ai/credits/pricing');
      setPackages(response.data.packages);
      if (response.data.packages.length > 0) {
        setSelectedPackage(response.data.packages[1] || response.data.packages[0]); // Select popular or first
      }
    } catch (error) {} finally {
      setLoading(false);
    }
  };

  const _handlePurchaseSuccess = (_packageInfo) => {
    setSuccess(true);
    setTimeout(() => {
      onPurchaseSuccess();
      onClose();
      setSuccess(false);
      setSelectedPackage(null);
    }, 2000);
  };

  const handlePurchaseError = (_error) => {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Purchase AI Credits</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : success ? (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Purchase Successful!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your credits have been added to your account.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a credit package:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              {packages.map((pkg) => (
                <Card
                  key={pkg.id}
                  sx={{
                    cursor: 'pointer',
                    border: selectedPackage?.id === pkg.id ? 2 : 1,
                    borderColor: selectedPackage?.id === pkg.id ? 'primary.main' : 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                      borderWidth: 2
                    }
                  }}
                  onClick={() => setSelectedPackage(pkg)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6">
                          {pkg.credits.toLocaleString()} Credits
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {pkg.description}
                        </Typography>
                      </Box>
                      <Typography variant="h6" color="primary">
                        £{pkg.price.toFixed(2)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {selectedPackage && (
              <Box>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const response = await api.post('/api/ai/credits/purchase', {
                        creditPackage: selectedPackage.id,
                        redirectTo
                      });
                      
                      // Redirect to Stripe checkout
                      window.location.href = response.data.url;
                    } catch (err) {
                      handlePurchaseError(err.response?.data?.message || 'Failed to create checkout session');
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading ? <CircularProgress size={24} /> : `Purchase for £${selectedPackage.price.toFixed(2)}`}
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading || success}>
          {success ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreditPurchaseModal;

